import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormToggle from '../../../components/form/FormToggle';
import {
  channelOptions,
  languageSelectOptions,
  listSectionOptions,
  processTypesOptions,
  webformOptions
} from '../../../utils/common';

import {
  outboundOptionGet,
  processAdd,
  processDetailsGetById,
  processUpdate,
  scriptsOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { crmsOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  audioPromptsAllOptionGet,
  ivrOptionsGet
} from '../../../store/slices/tenantSlices/incomingSlice';

const AddEditProcessModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null,
  setOpenCrmAddModal
}) => {
  const dispatch = useDispatch();
  const [crmOptions, setCrmOptions] = useState([]);
  const [outboundRoutesOptions, setOutboundRoutesOptions] = useState([]);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);

  const initialValues = {
    name: '',
    active: 'true',
    description: '',
    type: '',
    autoWrapup: 0,
    autoMissed: 0,
    autoOutbound: 15,
    dnc: 'true',
    numberMasking: 'true',
    listSection: '',
    script: '',
    webform: [],
    channel: 0,
    taborder: '',
    bufferlevel: 0,
    outboundroutes: '',
    queue: 0,
    crm: '',
    ProgressiveDialingSec: 0,
    agentwisedialing: 'false',
    welcome_prompt: '',
    waiting_prompt: '',
    forwarding_type: '',
    forwarding_options: '',
    chat_with_user: 'false',
    chat_with_supervisor: 'false',
    machine_detection: 'no',
    is_auto_audit: 'false',
    is_auto_lang: 'en'
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    type: Yup.string().required('Type is required'),
    outboundroutes: Yup.string().required('Outbound routes is required'),
    crm: Yup.string().required('CRM is required'),
    agentwisedialing: Yup.boolean().required('Agent wise dialing is required')
    // autoOutbound: Yup.number()
    //   .required('Auto Outbound is required')
    //   .min(5, 'Auto Outbound should be 5 or more')
  });

  const handlePayload = (values) => {
    const payload = { ...values };
    payload['webform'] = values?.webform?.join(',');

    // Remove fields based on type
    if (
      ['OutboundAndPredictive', 'OutboundAndPreview', 'OutboundAndProgressive'].includes(
        values.type
      )
    ) {
      delete payload['welcome_prompt'];
      delete payload['waiting_prompt'];
      delete payload['forwarding_options'];
      delete payload['forwarding_type'];
    }

    if (values?.type === 'VoiceBroadcast') {
      delete payload['welcome_prompt'];
      delete payload['waiting_prompt'];
      // delete payload['crm'];
      // delete payload['autoWrapup'];
      // delete payload['autoMissed'];
      // delete payload['dnc'];
      // delete payload['numberMasking'];
    }

    return payload;
  };

  const handleResponse = (response, actionType) => {
    if (response?.message) {
      toast.success(`${actionType} Successfully`);
      onCloseClick(true);
      resetForm();
    } else {
      toast.error(response.error);
    }
  };

  const onSubmit = async () => {
    const actionType = isEdit ? 'Record Updated' : 'Record Added';
    const payload = isEdit
      ? { ...handlePayload(values), id: editRecord.id }
      : handlePayload(values);

    try {
      const action = isEdit ? processUpdate(payload) : processAdd(payload);
      const response = await dispatch(action).unwrap();
      handleResponse(response, actionType);
    } catch (error) {
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getScriptOption = async () => {
    try {
      const response = await dispatch(scriptsOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setScriptOptions(options);
      } else {
        setScriptOptions([]);
      }
    } catch (error) {
      setScriptOptions([]);
      console.log('error', error);
    }
  };

  const getOutboundRoutesOption = async () => {
    try {
      const response = await dispatch(outboundOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setOutboundRoutesOptions(options);
      } else {
        setOutboundRoutesOptions([]);
      }
    } catch (error) {
      setOutboundRoutesOptions([]);
      console.log('error', error);
    }
  };

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getScriptOption();
      getCrmsOption();
      getAudioPromptOption();
      getOutboundRoutesOption();
      getIvrOption();
    }
  }, [isOpen]);

  const fetchProcessRecord = async () => {
    try {
      const response = await dispatch(processDetailsGetById(editRecord?.id)).unwrap();
      setValues({
        ...response,
        webform: response?.webform?.split(',') || [],
        active: String(response?.active),
        dnc: String(response?.dnc) || 'true',
        numberMasking: String(response?.numberMasking),
        agentwisedialing: String(response?.agentwisedialing),
        outboundroutes: Number(response?.outboundroutes) || '',
        forwarding_options: Number(response?.forwarding_options) || '',
        chat_with_supervisor: String(response?.chat_with_supervisor) || 'false',
        chat_with_user: String(response?.chat_with_user) || 'false',
        is_auto_audit: String(response?.is_auto_audit) || 'false',
        machine_detection: String(response?.machine_detection) || 'no',
        is_auto_lang: response?.is_auto_lang || 'en'
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchProcessRecord();
    }
  }, [editRecord]);

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    return '';
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Process' : 'Add Process'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="crm"
                label="CRM"
                placeholder="Select CRM"
                value={values.crm}
                options={crmOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isAdd={true}
                createName="Add CRMS"
                onCreateClick={() => {
                  setOpenCrmAddModal(true);
                  onCloseClick();
                }}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="channel"
                label="Dial Level"
                placeholder="Select Dial Level"
                value={values.channel}
                options={channelOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="bufferlevel"
                label="Buffer Level"
                value={values.bufferlevel}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                min={0}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="type"
                label="Type"
                placeholder="Select Type"
                value={values.type}
                options={
                  process.env.REACT_APP_VOICE_BROADCAST === 'true'
                    ? [
                        ...processTypesOptions,
                        {
                          label: 'Broadcast',
                          options: [{ label: 'VoiceBroadcast', value: 'VoiceBroadcast' }]
                        }
                      ]
                    : [...processTypesOptions]
                }
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue('agentwisedialing', 'false');
                  setFieldValue('ProgressiveDialingSec', 0);
                  setFieldValue('welcome_prompt', '');
                  setFieldValue('waiting_prompt', '');
                  setFieldValue('forwarding_type', '');
                  setFieldValue('forwarding_options', '');
                  if (e.target.value === 'VoiceBroadcast') {
                    setFieldValue('script', '');
                    setFieldValue('taborder', '');
                    setFieldValue('webform', []);
                    setFieldValue('autoWrapup', 0);
                    setFieldValue('queue', 0);
                    setFieldValue('channel', 0);
                    setFieldValue('autoMissed', 0);
                    setFieldValue('autoOutbound', 15);
                    setFieldValue('dnc', 'true');
                    setFieldValue('numberMasking', 'true');
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isGroup
                menuPlacement="bottom"
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoWrapup"
                  label="Auto Wrapup"
                  value={values.autoWrapup}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoMissed"
                  label="Auto Missed"
                  value={values.autoMissed}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoOutbound"
                  label="Auto Outbound"
                  value={values.autoOutbound}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormToggle
                  name="dnc"
                  label="DNC"
                  value={values.dnc}
                  onChange={(e) => setFieldValue('dnc', e)}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormToggle
                  name="numberMasking"
                  label="Number Masking"
                  value={values.numberMasking}
                  onChange={(e) => setFieldValue('numberMasking', e)}
                />
              </Col>
            )}
            <Col lg="3">
              <FormSelect
                name="listSection"
                label="List Section"
                placeholder="Select List Section"
                value={values.listSection}
                options={listSectionOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormSelect
                  name="script"
                  label="Script"
                  placeholder="Select Script"
                  value={values.script}
                  options={scriptOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormSelect
                  name="webform"
                  label="Webform"
                  placeholder="Select Webform"
                  value={values.webform}
                  options={webformOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  isMulti
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormInput
                  name="taborder"
                  label="Tab Order"
                  placeholder="Enter tab order"
                  value={values.taborder}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormSelect
                name="outboundroutes"
                label="Outbound Routes"
                placeholder="Select Outbound Routes"
                value={values.outboundroutes}
                options={outboundRoutesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="queue"
                  label="queue"
                  value={values.queue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== '' &&
              (values.type === 'BlendedAndProgressive' ||
                values.type === 'OutboundAndProgressive') && (
                <Col lg="3">
                  <FormNumberInput
                    name="ProgressiveDialingSec"
                    label="Progressive Dialing Sec"
                    value={values.ProgressiveDialingSec}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    min={0}
                  />
                </Col>
              )}
            {values.type !== '' &&
              (values.type === 'OutboundAndPreview' ||
                values.type === 'OutboundAndProgressive' ||
                values.type === 'BlendedAndPreview' ||
                values.type === 'BlendedAndProgressive') && (
                <Col lg="3">
                  <FormToggle
                    name="agentwisedialing"
                    label="Agent Wise Dialing"
                    value={values.agentwisedialing}
                    onChange={(e) => setFieldValue('agentwisedialing', e)}
                  />
                </Col>
              )}
            <Col lg="3">
              <FormToggle
                name="chat_with_user"
                label="Chat with user"
                value={values.chat_with_user}
                onChange={(e) => setFieldValue('chat_with_user', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="chat_with_supervisor"
                label="Chat with supervisor"
                value={values.chat_with_supervisor}
                onChange={(e) => setFieldValue('chat_with_supervisor', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_auto_audit"
                label="Auto Audit"
                value={values.is_auto_audit}
                onChange={(e) => setFieldValue('is_auto_audit', e)}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="machine_detection"
                label="Machine Detection"
                value={String(values.machine_detection === 'yes')}
                onChange={() => {
                  setFieldValue(
                    'machine_detection',
                    values.machine_detection === 'yes' ? 'no' : 'yes'
                  );
                }}
              />
            </Col>
            {values.is_auto_audit === 'true' && (
              <Col lg="3">
                <FormSelect
                  name="is_auto_lang"
                  label="Auto Language"
                  placeholder="Select auto language"
                  value={values.is_auto_lang}
                  options={languageSelectOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
          </Row>
          {values.type !== '' &&
            !['OutboundAndPredictive', 'OutboundAndPreview', 'OutboundAndProgressive'].includes(
              values.type
            ) && (
              <React.Fragment>
                <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                  <div>
                    {values.type === 'VoiceBroadcast'
                      ? 'Broadcast Configuration'
                      : 'Incoming Configuration'}
                  </div>
                </div>
                {values.type === 'VoiceBroadcast' ? (
                  <Row className="py-2">
                    <Col lg="3" md="3" className="flex-row">
                      <FormSelect
                        name="forwarding_type"
                        label="Forwarding Type"
                        placeholder="Select Forwarding Type"
                        value={values.forwarding_type}
                        options={[
                          { label: 'Transfer To IVR', value: 'transfer_to_ivr' },
                          { label: 'Playback', value: 'transfer_to_playback' }
                        ]}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value === 'transfer_to_hangup') {
                            setFieldValue('forwarding_options', 'Hangup');
                          } else {
                            setFieldValue('forwarding_options', '');
                          }
                        }}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    {values.forwarding_type !== '' ? (
                      <Col lg="3" md="3" className="flex-row">
                        <FormSelect
                          name="forwarding_options"
                          label={getForwardingLabel(values.forwarding_type)}
                          placeholder={`Select ${getForwardingLabel(values.forwarding_type)}`}
                          value={values.forwarding_options}
                          options={getForwardingOptions(values.forwarding_type)}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          touched={touched}
                          errors={errors}
                        />
                      </Col>
                    ) : null}
                  </Row>
                ) : (
                  <Row className="py-2">
                    <Col lg="3">
                      <FormSelect
                        name="welcome_prompt"
                        label="Welcome Prompt"
                        placeholder="Select Welcome Prompt"
                        value={values.welcome_prompt}
                        options={audioPromptOption}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="waiting_prompt"
                        label="Waiting Prompt"
                        placeholder="Select Waiting Prompt"
                        value={values.waiting_prompt}
                        options={audioPromptOption}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditProcessModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any,
  setOpenCrmAddModal: PropTypes.func
};

export default AddEditProcessModal;
